import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Check, ArrowRight, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
});

const cents = (c) => `$${(c / 100).toFixed(2)}`;

/**
 * Pay-Per-Play pricing — fetched live from /api/plays/tiers/active so
 * the marketing surface can never drift from what a customer actually pays.
 * Light-panel treatment for a modern, premium feel that breaks up the
 * dark navy sections either side.
 */
export const PricingPlays = ({ onBegin }) => {
    const [tiers, setTiers] = useState([]);
    const [minPurchase, setMinPurchase] = useState(200);
    const [featured, setFeatured] = useState(null);

    useEffect(() => {
        axios.get(`${API}/plays/tiers/active`)
            .then(({ data }) => {
                const list = (data.tiers || []).sort((a, b) => a.min_plays - b.min_plays);
                setTiers(list);
                setMinPurchase(data.min_purchase || 200);
                // Feature the mid-tier by default (usually best perceived value)
                const mid = list[Math.min(Math.floor(list.length / 2), list.length - 1)];
                setFeatured(mid?.id);
            })
            .catch(() => setTiers([]));
    }, []);

    return (
        <section
            id="pricing"
            data-testid="landing-pricing-plays"
            className="relative py-24 lg:py-32 bg-gradient-to-b from-white via-slate-50 to-white text-slate-900 overflow-hidden"
        >
            {/* Ambient glow so the section still feels alive against the light background */}
            <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full opacity-25 blur-[130px]"
                style={{ background: "radial-gradient(circle, rgba(255,90,0,0.55), rgba(255,90,0,0) 60%)" }} />
            <div className="pointer-events-none absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full opacity-20 blur-[90px]"
                style={{ background: "radial-gradient(circle, rgba(15,30,59,0.6), transparent 60%)" }} />

            <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
                <motion.div {...fadeUp(0)} className="text-center max-w-3xl mx-auto mb-14 lg:mb-20">
                    <div className="font-mono-tech text-[11px] uppercase tracking-[0.35em] text-[#FF5A00] mb-6 inline-flex items-center gap-3">
                        <span className="inline-block w-6 h-px bg-[#FF5A00]" />
                        06 / Pay-per-play pricing
                        <span className="inline-block w-6 h-px bg-[#FF5A00]" />
                    </div>
                    <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-7xl leading-[0.95] text-slate-950">
                        You buy plays.<br />
                        <span className="text-slate-400">You don&apos;t buy air.</span>
                    </h2>
                    <p className="mt-6 text-slate-600 text-base sm:text-lg leading-relaxed">
                        Every play is a spot on a live screen. Buy more, pay less per
                        play — and only ever pay for the plays you actually purchase.
                        Prices below come straight from our live pricing engine.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4">
                    {tiers.map((t, i) => {
                        const isFeatured = t.id === featured;
                        const priceStr = cents(t.price_per_play_cents);
                        return (
                            <motion.div
                                key={t.id}
                                {...fadeUp(0.05 * i)}
                                onMouseEnter={() => setFeatured(t.id)}
                                onFocus={() => setFeatured(t.id)}
                                tabIndex={0}
                                data-testid={`pricing-tier-${t.id}`}
                                className={`group relative p-6 lg:p-7 rounded-2xl flex flex-col transition-all duration-500 cursor-pointer border ${
                                    isFeatured
                                        ? "bg-slate-950 text-white border-slate-950 shadow-2xl shadow-slate-950/20 lg:-translate-y-3 scale-100"
                                        : "bg-white/70 backdrop-blur border-slate-200 hover:border-slate-300 hover:-translate-y-1 shadow-sm shadow-slate-900/5"
                                }`}
                            >
                                {isFeatured && (
                                    <div className="absolute -top-3 left-6 bg-[#FF5A00] text-white text-[10px] font-mono-tech uppercase tracking-[0.25em] px-3 py-1 rounded-full inline-flex items-center gap-1 shadow-sm">
                                        <Sparkles size={10} /> Popular
                                    </div>
                                )}
                                <div className={`font-mono-tech text-[10px] uppercase tracking-[0.3em] ${isFeatured ? "text-[#FF5A00]" : "text-slate-500"}`}>
                                    {t.label}
                                </div>
                                <div className={`mt-2 font-mono-tech text-xs ${isFeatured ? "text-slate-300" : "text-slate-500"}`}>
                                    {t.min_plays.toLocaleString()}{t.max_plays ? `–${t.max_plays.toLocaleString()}` : "+"} plays
                                </div>
                                <div className={`mt-6 font-display font-black tracking-tighter ${isFeatured ? "text-white" : "text-slate-950"}`}>
                                    <span className="text-5xl">{priceStr}</span>
                                </div>
                                <div className={`mt-1 text-xs ${isFeatured ? "text-slate-400" : "text-slate-500"}`}>
                                    per play {t.max_plays == null && <span className={isFeatured ? "text-[#FF5A00]" : "text-[#FF5A00]"}>from</span>}
                                </div>
                                <div className={`mt-6 pt-6 border-t space-y-2 text-xs flex-1 ${isFeatured ? "border-white/10 text-slate-300" : "border-slate-200 text-slate-600"}`}>
                                    <Feature invert={isFeatured} label={`Total from ${cents(t.min_plays * t.price_per_play_cents)}`} />
                                    <Feature invert={isFeatured} label="No lock-ins" />
                                    <Feature invert={isFeatured} label="Same-day booking" />
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                <motion.div {...fadeUp(0.35)} className="mt-14 lg:mt-20 text-center">
                    <button
                        onClick={onBegin}
                        data-testid="landing-pricing-cta"
                        className="landing-hero-dark group inline-flex items-center gap-3 bg-slate-950 hover:bg-slate-900 text-white px-9 py-5 rounded-full text-sm font-semibold uppercase tracking-widest transition-all duration-300 relative overflow-hidden shadow-lg shadow-slate-950/20"
                    >
                        <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                        <span className="relative">Put Me On A Billboard</span>
                        <ArrowRight size={16} className="relative transition-transform group-hover:translate-x-1" />
                    </button>
                    <p className="mt-5 text-xs text-slate-500 font-mono-tech uppercase tracking-widest">
                        Minimum {minPurchase.toLocaleString()} plays · GST added at checkout
                    </p>
                </motion.div>
            </div>
        </section>
    );
};

const Feature = ({ label, invert }) => (
    <div className="flex items-center gap-2">
        <Check size={12} className={invert ? "text-[#FF5A00]" : "text-emerald-500"} />
        <span>{label}</span>
    </div>
);
